import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { animated, useTransition } from '@react-spring/web';
import * as React from 'react';
import styled from 'styled-components';

import useCurrencyConversion from '@/pages/BiddingConsole/hooks/useCurrencyConversion';
import { useAppSelector } from '@/redux/hooks';
import { getLiveItemDataLeadingBid } from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCurrency,
    getItemSummaryStartPrice,
} from '@/redux/modules/item/summary/itemSummary.selectors';

import useShowMobileConsole from '../../hooks/useShowMobileConsole';

export type CurrentBidProps = {
    className?: string;
    itemId: number;
    selectedCurrency: string;
};

const CurrentBid = ({ className, itemId, selectedCurrency = 'usd' }: CurrentBidProps) => {
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const leadingBid = useAppSelector((state) => getLiveItemDataLeadingBid(state, itemId));
    const showMobileConsole = useShowMobileConsole();

    const { showConversion, targetCurrencyCode, valueInTargetCurrency } = useCurrencyConversion({
        catalogId,
        selectedCurrency,
    });

    //Maintain single element array for bid transition*
    const currentBid = [{ value: leadingBid || startPrice }];
    const transitions = useTransition(currentBid, {
        enter: { opacity: 1, transform: 'translateY(0px)' },
        from: {
            opacity: 0,
            transform: showMobileConsole ? 'translateY(29px)' : 'translateY(58px)',
        },
        initial: { opacity: 1, transform: 'translateY(0px)' },
        keys: (bid) => bid.value,
        leave: {
            opacity: 0,
            transform: showMobileConsole ? 'translateY(-29px)' : 'translateY(-58px)',
        },
    });

    return (
        <>
            <StyledCurrentBid className={className}>
                {leadingBid || startPrice ? (
                    <StyledCurrentBidWrapper>
                        {transitions(({ ...style }, bid) => (
                            <StyledAnimationDiv style={style}>
                                <OriginalValue>
                                    <FormattedNumber
                                        currency={currency}
                                        key="current-bid-original-value"
                                        maximumFractionDigits={0}
                                        minimumFractionDigits={0}
                                        style="currency"
                                        value={bid.value}
                                    />
                                </OriginalValue>
                            </StyledAnimationDiv>
                        ))}
                    </StyledCurrentBidWrapper>
                ) : (
                    <DashLayout>—</DashLayout>
                )}
                {showConversion && (
                    <ConvertedValue
                        as="h5"
                        color="weak"
                    >
                        <FormattedNumber
                            currency={targetCurrencyCode}
                            key="current-bid-converted-value"
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            style="currency"
                            value={valueInTargetCurrency}
                        />
                    </ConvertedValue>
                )}
            </StyledCurrentBid>
        </>
    );
};

export default CurrentBid;

const DashLayout = styled.div`
    position: relative;
    display: flex;
    justify-content: flex-end;
`;

const StyledAnimationDiv = styled(animated.div)`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const StyledCurrentBidWrapper = styled.div`
    height: 58px;
    position: relative;
    overflow: hidden;

    @media (max-width: 998px) {
        height: 29px;
    }
`;

const StyledCurrentBid = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    border-radius: 8px;
    overflow: hidden;
    padding: 10px 16px;
    background-color: ${({ theme }) => theme.colors.white};

    @media (max-width: 998px) {
        padding: 8px 16px;
    }
`;

const OriginalValue = styled.div`
    color: ${({ theme }) => theme.colors.black};
    font-size: 48px;
    line-height: 58px;
    text-align: right;
    font-weight: 500;

    @media (max-width: 998px) {
        font-size: 24px;
        line-height: 29px;
    }
`;

const ConvertedValue = styled(H5)`
    text-align: right;
    font-weight: 500;
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
    margin-top: 8px;
    padding-top: 8px;
    && {
        font-size: 16px;
        line-height: 22px;
    }
`;
